import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import type { ComponentProps, ReactNode, RefObject } from 'react'

import { EmojiPicker as PopcornEmojiPicker } from '@buffer-mono/popcorn'

import {
  COMPOSER_EMOJI_FREQUENCY_STORE,
  updateEmojiFrequency,
  getFrequentlyUsedEmojis,
  initializeEmojiFrequency,
} from '~publish/helpers/emojiFrequency'
import type { EmojiFrequencyStore } from '~publish/helpers/emojiFrequency'

const EMOJIS_PER_ROW = 9
const MAX_FREQUENTLY_USED = 3 * EMOJIS_PER_ROW

const haveEqualEmojis = (left: string[], right: string[]): boolean =>
  left.length === right.length &&
  left.every((emoji, index) => emoji === right[index])

// Migrating and healing the Composer's store runs once per session, not once
// per picker: a page of Notes alone mounts two pickers for every Note.
let composerStoreReady: Promise<void> | undefined
const prepareComposerStore = (): Promise<void> => {
  composerStoreReady ??= initializeEmojiFrequency().catch(() => {
    // Keep the picker usable if persisted frequency data cannot be initialized.
  })
  return composerStoreReady
}

type PopcornEmojiPickerProps = ComponentProps<typeof PopcornEmojiPicker>

/**
 * The emoji picker with the account's frequently used emoji. It reads them
 * when it opens rather than when it mounts, since most pickers on a page are
 * never opened.
 */
export const EmojiPicker = ({
  onEmojiSelect,
  emojiPickerRef,
  children,
  open,
  onOpenChange,
  side,
  align,
  frequencyStore = COMPOSER_EMOJI_FREQUENCY_STORE,
  recordsSelections = true,
}: {
  onEmojiSelect: (emoji: string) => void
  emojiPickerRef?: RefObject<HTMLDivElement>
  children: ReactNode
  /** Where the frequently used emoji come from. Defaults to the Composer's. */
  frequencyStore?: EmojiFrequencyStore
  /**
   * Whether each selection counts as a use. A caller that records uses
   * itself, such as Note reactions counting only additions, passes false.
   */
  recordsSelections?: boolean
} & Pick<
  PopcornEmojiPickerProps,
  'open' | 'onOpenChange' | 'side' | 'align'
>): JSX.Element => {
  const readFrequentEmojis = useCallback(
    (): string[] =>
      getFrequentlyUsedEmojis(MAX_FREQUENTLY_USED, frequencyStore),
    [frequencyStore],
  )
  const [frequentlyUsedEmojis, setFrequentlyUsedEmojis] = useState<string[]>(
    () => (open ? readFrequentEmojis() : []),
  )
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return (): void => {
      mountedRef.current = false
    }
  }, [])

  const refresh = useCallback((): void => {
    setFrequentlyUsedEmojis((currentEmojis) => {
      const nextEmojis = readFrequentEmojis()
      return haveEqualEmojis(currentEmojis, nextEmojis)
        ? currentEmojis
        : nextEmojis
    })
  }, [readFrequentEmojis])

  const prepare = useCallback((): void => {
    refresh()
    if (frequencyStore !== COMPOSER_EMOJI_FREQUENCY_STORE) return
    void (async (): Promise<void> => {
      await prepareComposerStore()
      if (mountedRef.current) refresh()
    })()
  }, [frequencyStore, refresh])

  // A caller can open the picker without a trigger click.
  useEffect(() => {
    if (open) prepare()
  }, [open, prepare])

  // Read before the opening render, so the row does not appear empty first.
  const handleOpenChange = (nextOpen: boolean): void => {
    if (nextOpen) prepare()
    onOpenChange?.(nextOpen)
  }

  const handleEmojiSelect = (emoji: string): void => {
    if (recordsSelections) {
      updateEmojiFrequency(emoji, frequencyStore)
      refresh()
    }
    onEmojiSelect(emoji)
  }

  return (
    <PopcornEmojiPicker
      ref={emojiPickerRef}
      onEmojiSelect={handleEmojiSelect}
      frequentlyUsedEmojis={frequentlyUsedEmojis}
      open={open}
      onOpenChange={handleOpenChange}
      side={side}
      align={align}
    >
      {children}
    </PopcornEmojiPicker>
  )
}
